import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api';
import Spinner from './Spinner';
import Modal from './Modal';
import { toast } from './Toast';
import GpsTracker, { NATIV_DISPONIBIL } from '../services/gpsTracker';
import CursaGpsMap, { esteGol } from './CursaGpsMap';

// In aplicatia Android (APK-ul nou) urmarirea GPS ruleaza in serviciul NATIV (GpsTrackerService):
// foloseste doar GPS-ul, salveaza punctele local si le trimite singur din 10 in 10 minute, cu
// ecranul stins sau cu alta aplicatie deschisa. In browser / PWA / un APK mai vechi se foloseste
// geolocatia browserului, care functioneaza doar cu pagina pe ecran (limitare a browserelor).

const CHEIE_COADA_LOCALA = 'gps_coada_puncte_netrimise'; // doar calea din browser
const ACURATETE_MAX_M = 15;      // doar browser: citirile cu raza de eroare mai mare se arunca (la fel ca pe server)
const ACURATETE_START_AVERTISMENT_M = 40; // la Start: peste atat avertizam ca nu avem semnal GPS precis
const DISTANTA_MIN_METRI = 20;   // pastram un punct nou doar dupa ce s-a miscat cel putin atat...
const DISTANTA_LENTA_METRI = 8;  // ...sau cel putin atat, daca au trecut TIMP_LENT_MS (miscare lenta)
const TIMP_LENT_MS = 30000;

function distantaMetri(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function citesteCoadaLocala() {
  try { return JSON.parse(localStorage.getItem(CHEIE_COADA_LOCALA) || '[]'); } catch { return []; }
}
function salveazaCoadaLocala(coada) {
  localStorage.setItem(CHEIE_COADA_LOCALA, JSON.stringify(coada));
}

// ==================== ISTORIC (curse încheiate) ====================
// Reutilizat atat pentru "cursele mele" (sofer) cat si pentru "toate cursele"
// (dispecer/admin, cu filtre optionale) - doar endpoint-ul si coloanele in plus diferă.
const MARIME_PAGINA_ISTORIC = 20;

function IstoricCurse({ endpoint, arataVehiculSofer }) {
  const [lista, setLista] = useState([]);
  const [pagina, setPagina] = useState(0);
  const [seIncarca, setSeIncarca] = useState(true);
  const [seIncarcaMaiMulte, setSeIncarcaMaiMulte] = useState(false);
  const [areMaiMulte, setAreMaiMulte] = useState(false);
  const [expandat, setExpandat] = useState(null);
  const [traseeCache, setTraseeCache] = useState({});
  const [filtruSofer, setFiltruSofer] = useState('');
  const [filtruVehicul, setFiltruVehicul] = useState('');
  const [optiuniVehicule, setOptiuniVehicule] = useState([]);
  const [optiuniSoferi, setOptiuniSoferi] = useState([]);

  // v68: dropdown real (nu text liber) - preluat din aceleasi liste folosite si in restul
  // aplicatiei pentru vehicule/soferi, ca sa alegi dintr-o lista, nu sa tastezi exact.
  useEffect(() => {
    if (!arataVehiculSofer) return;
    api.get('/api/auto/nruri').then(({ data }) => setOptiuniVehicule(data || [])).catch(() => {});
    api.get('/api/personal/soferi').then(({ data }) => setOptiuniSoferi(data || [])).catch(() => {});
  }, [arataVehiculSofer]);

  // suprascrieri optionale, ca sa nu citim valori "vechi" din state imediat dupa un
  // onChange de select (setState e asincron - fara asta, primul filtru aplicat ar
  // intarzia cu o selectie in urma).
  const incarcaPagina = useCallback(async (paginaDeIncarcat, resetare, suprascrieri) => {
    if (resetare) setSeIncarca(true); else setSeIncarcaMaiMulte(true);
    try {
      const params = { pagina: paginaDeIncarcat, marime: MARIME_PAGINA_ISTORIC };
      if (arataVehiculSofer) {
        const sofer = (suprascrieri?.username ?? filtruSofer).trim();
        const vehicul = (suprascrieri?.nrInmatriculare ?? filtruVehicul).trim();
        if (sofer) params.username = sofer;
        if (vehicul) params.nrInmatriculare = vehicul;
      }
      const { data } = await api.get(endpoint, { params });
      setLista(prev => resetare ? data : [...prev, ...data]);
      setAreMaiMulte(data.length === MARIME_PAGINA_ISTORIC);
      setPagina(paginaDeIncarcat);
    } catch (e) {
      toast(e.response?.data?.error || 'Nu am putut încărca istoricul curselor.', 'error');
    } finally {
      setSeIncarca(false);
      setSeIncarcaMaiMulte(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, arataVehiculSofer]);

  useEffect(() => { incarcaPagina(0, true); }, [incarcaPagina]);

  const toggleExpandat = async (c) => {
    if (expandat === c.id) { setExpandat(null); return; }
    setExpandat(c.id);
    if (!traseeCache[c.id]) {
      try {
        const { data } = await api.get(`/api/cursa-gps/${c.id}/traseu`);
        setTraseeCache(prev => ({ ...prev, [c.id]: data.puncte || [] }));
      } catch {
        setTraseeCache(prev => ({ ...prev, [c.id]: [] }));
      }
    }
  };

  const nrColoane = arataVehiculSofer ? 6 : 4;

  return (
    <div>
      {arataVehiculSofer && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <select value={filtruVehicul} style={{ padding: 6, flex: '1 1 160px' }}
            onChange={e => { setFiltruVehicul(e.target.value); incarcaPagina(0, true, { nrInmatriculare: e.target.value, username: filtruSofer }); }}>
            <option value="">Toate vehiculele</option>
            {optiuniVehicule.map(v => <option key={v.nrInmatriculare} value={v.nrInmatriculare}>{v.nrInmatriculare}</option>)}
          </select>
          <select value={filtruSofer} style={{ padding: 6, flex: '1 1 160px' }}
            onChange={e => { setFiltruSofer(e.target.value); incarcaPagina(0, true, { nrInmatriculare: filtruVehicul, username: e.target.value }); }}>
            <option value="">Toți șoferii</option>
            {optiuniSoferi.map(s => <option key={s.username} value={s.username}>{s.username}</option>)}
          </select>
          {(filtruVehicul || filtruSofer) && (
            <button className="btn" onClick={() => { setFiltruVehicul(''); setFiltruSofer(''); incarcaPagina(0, true, { nrInmatriculare: '', username: '' }); }}>
              ✕ Resetează
            </button>
          )}
        </div>
      )}
      {seIncarca ? <Spinner /> : lista.length === 0 ? (
        <p style={{ color: '#888', textAlign: 'center', padding: 16 }}>Nicio cursă încheiată încă.</p>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: 6 }}>Dată</th>
                {arataVehiculSofer && <th style={{ padding: 6 }}>Vehicul</th>}
                {arataVehiculSofer && <th style={{ padding: 6 }}>Șofer</th>}
                <th style={{ padding: 6 }}>De unde → până unde</th>
                <th style={{ padding: 6 }}>Ora</th>
                <th style={{ padding: 6 }}>Distanță</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(c => (
                <React.Fragment key={c.id}>
                  <tr style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => toggleExpandat(c)}>
                    <td style={{ padding: 6 }}>{c.dataStart}</td>
                    {arataVehiculSofer && <td style={{ padding: 6 }}>{c.nrInmatriculare}</td>}
                    {arataVehiculSofer && <td style={{ padding: 6 }}>{c.username}</td>}
                    <td style={{ padding: 6 }}>{c.adresaStart || '—'} → {c.adresaStop || '—'}</td>
                    <td style={{ padding: 6 }}>{c.oraStart}{c.oraStop ? `–${c.oraStop}` : ''}</td>
                    <td style={{ padding: 6 }}>{Number(c.distantaGpsKm || 0).toFixed(1)} km</td>
                  </tr>
                  {expandat === c.id && (
                    <tr>
                      <td colSpan={nrColoane} style={{ padding: '8px 6px' }}>
                        <CursaGpsMap
                          trasee={[{ id: c.id, puncte: traseeCache[c.id] || [], eticheta: `${c.nrInmatriculare} — ${c.username}` }]}
                          inaltime={280}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {areMaiMulte && (
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <button className="btn" disabled={seIncarcaMaiMulte} onClick={() => incarcaPagina(pagina + 1, false)}>
                {seIncarcaMaiMulte ? 'Se încarcă...' : 'Încarcă mai multe'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== VEDERE ȘOFER ====================

// Adresa serverului, transmisa si serviciului nativ (care trimite punctele singur, fara WebView).
const BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;
// Nativ: serviciul Android trimite punctele la fiecare 10 minute, chiar cu ecranul stins.
const INTERVAL_TRIMITERE_FUNDAL_MS = 10 * 60 * 1000;
// Cat timp ecranul e pornit: trimitem si reimprospatam harta mai des (dispecerul vede cursa "live").
const INTERVAL_CICLU_ECRAN_MS = 30 * 1000;
const MARIME_LOT_BROWSER = 500;

// O singura operatie de trimitere la un moment dat (ciclul de pe ecran si Stop nu se calca).
let coadaProcesare = Promise.resolve();
function serializat(fn) {
  const urmatoarea = coadaProcesare.then(fn, fn);
  coadaProcesare = urmatoarea.catch(() => {});
  return urmatoarea;
}

const pauza = (ms) => new Promise(r => setTimeout(r, ms));

// Text scurt, pentru diagnostic - ca sa stim EXACT de ce a esuat o trimitere.
function descrieEroare(e) {
  if (!e.response) return 'conexiune (offline sau server inaccesibil)';
  return `${e.response.status} ${e.response.data?.error || e.response.statusText || ''}`.trim();
}

function VedereSofer({ onNavigateToFoaie, onCursaActiva }) {
  const [cursa, setCursa] = useState(null);
  const [puncte, setPuncte] = useState([]);
  const [seIncarca, setSeIncarca] = useState(true);
  const [sePorneste, setSePorneste] = useState(false);
  const [seOpreste, setSeOpreste] = useState(false);
  const [avertismentStop, setAvertismentStop] = useState(null); // { foaieParcursId, distantaGpsKm }
  const [kmStart, setKmStart] = useState('');           // camp optional, la Start
  const [cereKmStop, setCereKmStop] = useState(null);   // { lat, lon } - deschide dialogul obligatoriu la Stop
  const [kmStopValoare, setKmStopValoare] = useState('');
  const [seSalveazaKmStop, setSeSalveazaKmStop] = useState(false);
  const [ultimaTrimitere, setUltimaTrimitere] = useState(null); // Date.now() al ultimei trimiteri reusite
  const [puncteNesincronizate, setPuncteNesincronizate] = useState(0);
  const [ultimaEroareSincronizare, setUltimaEroareSincronizare] = useState(null); // text scurt, diagnostic
  const [gpsActiv, setGpsActiv] = useState(true);
  // Pozitia "live" a cursorului: luata LOCAL de la serviciul nativ (fara server), la fiecare citire GPS noua.
  const [pozitieLive, setPozitieLive] = useState(null);
  const ultimaCitireLiveRef = useRef(0);
  const [ecranAprins, setEcranAprins] = useState(null); // null = nu stim inca; true/false = reusit/esuat

  // v77: navigatie ca ajutor (destinatie optionala). Campul apare DOAR daca backend-ul are cheia
  // OpenRouteService configurata ("activ" din /api/navigatie/config) - fara cheie ramane ascuns.
  const [navActiv, setNavActiv] = useState(false);
  const [qDest, setQDest] = useState('');
  const [rezultateDest, setRezultateDest] = useState(null);
  const [seCauta, setSeCauta] = useState(false);
  const [destinatie, setDestinatie] = useState(null);   // { nume, lat, lon }
  const [ruta, setRuta] = useState(null);               // { coord:[[lat,lon]], distantaKm, durataMin, totalM }
  const [rutaEroare, setRutaEroare] = useState(null);
  const [pornitAcum, setPornitAcum] = useState(false);  // cursa tocmai pornita -> harta intra pe ecran complet
  const ultimaRecalcRef = useRef(0);
  const deviatiiRef = useRef(0);

  // v78: semnal GPS pierdut / revenit (doar aplicatia Android noua, care trimite citiri continue)
  const [semnalPierdutDe, setSemnalPierdutDe] = useState(null); // secunde de cand n-am citire buna, sau null
  const [infoSemnal, setInfoSemnal] = useState(null);           // { text, pana } dupa revenirea semnalului
  const semnalRef = useRef({ pierdut: false, lat: null, lon: null, la: 0 });
  const puncteRef = useRef([]);
  useEffect(() => { puncteRef.current = puncte; }, [puncte]);
  const [, forteazaRerandare] = useState(0); // doar ca sa reimprospatam textul "acum X secunde" periodic

  const watchIdRef = useRef(null);       // doar browser
  const ultimTrimisRef = useRef(null);   // doar browser: ultimul punct pastrat {lat, lon, timp}
  const ultimaPozitieRef = useRef(null); // doar browser: ultima pozitie buna (pentru Stop)
  const wakeLockRef = useRef(null);      // doar browser
  const ascunsLaRef = useRef(null);      // doar browser
  const ultimIdPunctRef = useRef(0);     // nativ: ultimul id de punct incarcat de pe server (pentru harta)
  const cursaRef = useRef(null);
  useEffect(() => { cursaRef.current = cursa; }, [cursa]);
  // Contul demo: parintele blocheaza alegerea soferului cat timp exista o cursa activa.
  useEffect(() => { if (onCursaActiva) onCursaActiva(!!cursa); }, [cursa, onCursaActiva]);

  useEffect(() => {
    api.get('/api/navigatie/config').then(({ data }) => setNavActiv(!!(data && data.activ))).catch(() => {});
  }, []);

  // Traseul se calculeaza o data ce exista cursa + destinatie (si dupa o reluare a cursei). O eroare
  // NU se reincearca singura (ca sa nu consumam cota zilnica degeaba) - apare butonul "Reincearca".
  useEffect(() => {
    if (!cursa || !destinatie || ruta || rutaEroare || !navActiv) return;
    if (Date.now() - ultimaRecalcRef.current < 15000) return;
    const ultim = puncte.length > 0 ? puncte[puncte.length - 1] : null;
    const lat = ultim ? ultim.lat : cursa.latStart;
    const lon = ultim ? ultim.lon : cursa.lonStart;
    if (lat == null || lon == null) return;
    calculeazaRuta(Number(lat), Number(lon));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursa, destinatie, ruta, rutaEroare, navActiv, puncte]);

  // Abatere de la traseu: dupa 2 pozitii consecutive la > 150 m de drum, recalculam din pozitia
  // curenta - dar cel mult o data la 3 minute (cota zilnica de trasee e comuna tuturor conturilor).
  useEffect(() => {
    if (!ruta || puncte.length === 0) return;
    const ultim = puncte[puncte.length - 1];
    const { dist } = celMaiAproape(ultim, ruta.coord);
    deviatiiRef.current = dist > 150 ? deviatiiRef.current + 1 : 0;
    if (deviatiiRef.current >= 2 && Date.now() - ultimaRecalcRef.current > 3 * 60 * 1000) {
      calculeazaRuta(Number(ultim.lat), Number(ultim.lon));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puncte]);

  // ---------------- trimitere puncte ----------------

  // Nativ: serviciul trimite singur la 10 minute, dar are nevoie de un token valid. Il
  // reinnoim din aplicatia web (care il reimprospateaza singura cand expira).
  async function trimiteTokenNativ() {
    const token = localStorage.getItem('token');
    if (!token) return;
    try { await GpsTracker.updateAuth({ token, baseUrl: BASE_URL }); } catch (e) { /* ignorat */ }
  }

  // Browser: trimite coada locala in loturi. Un lot se sterge DOAR dupa raspuns 200.
  async function golesteBrowser(cursaId) {
    let eroare = '';
    let coada = citesteCoadaLocala();
    while (coada.length > 0) {
      const lot = coada.slice(0, MARIME_LOT_BROWSER);
      try {
        const { data } = await api.post(`/api/cursa-gps/${cursaId}/puncte`, {
          puncte: lot.map(i => ({ lat: i.lat, lon: i.lon, viteza: i.viteza, acuratete: i.acuratete, ts: i.ts })),
        });
        salveazaCoadaLocala(citesteCoadaLocala().slice(lot.length));
        if (data && data.distantaGpsKm != null) {
          setCursa(c => c ? { ...c, distantaGpsKm: data.distantaGpsKm } : c);
        }
        setUltimaTrimitere(Date.now());
        coada = citesteCoadaLocala();
      } catch (e) {
        if (e.response && e.response.status === 404) { // cursa nu mai e activa pe server
          salveazaCoadaLocala([]);
          eroare = '404 cursa nu mai e activă';
        } else {
          eroare = descrieEroare(e);
        }
        break;
      }
    }
    const pending = citesteCoadaLocala().length;
    setPuncteNesincronizate(pending);
    setUltimaEroareSincronizare(eroare || null);
    return { pending, eroare, cursaInchisa: eroare.startsWith('404') };
  }

  // Trimite acum tot ce e in asteptare. Intoarce { pending, eroare, cursaInchisa }.
  function goleste() {
    return serializat(async () => {
      const c = cursaRef.current;
      if (!c) return { pending: 0, eroare: '', cursaInchisa: false };
      if (NATIV_DISPONIBIL) {
        await trimiteTokenNativ();
        const r = await GpsTracker.flushNow();
        if (r.distantaGpsKm != null) {
          setCursa(x => x ? { ...x, distantaGpsKm: r.distantaGpsKm } : x);
        }
        if (r.trimise > 0) setUltimaTrimitere(Date.now());
        setPuncteNesincronizate(r.pending || 0);
        setUltimaEroareSincronizare(r.eroare || null);
        return { pending: r.pending || 0, eroare: r.eroare || '', cursaInchisa: !!r.cursaInchisa };
      }
      return golesteBrowser(c.id);
    });
  }

  // Nativ: aduce de pe server doar punctele aparute de la ultima incarcare - pentru harta de pe ecran.
  async function incarcaPuncteNoi(cursaId) {
    const { data } = await api.get(`/api/cursa-gps/${cursaId}/traseu`, { params: { dupaId: ultimIdPunctRef.current } });
    const noi = data.puncte || [];
    if (noi.length > 0) {
      ultimIdPunctRef.current = noi[noi.length - 1].id;
      setPuncte(p => [...p, ...noi]);
    }
    if (data.distantaGpsKm != null) {
      setCursa(c => c ? { ...c, distantaGpsKm: data.distantaGpsKm } : c);
    }
  }

  // Ciclul de pe ecran: trimite ce s-a strans, apoi (nativ) actualizeaza harta. Un ciclu esuat
  // nu opreste nimic - urmatorul il reia.
  async function ciclu() {
    const c = cursaRef.current;
    if (!c) return;
    try {
      await goleste();
      if (NATIV_DISPONIBIL) await incarcaPuncteNoi(c.id);
    } catch (e) { /* reincercat la urmatorul ciclu */ }
  }
  const cicluRef = useRef(null);
  cicluRef.current = ciclu;

  // ---------------- ecran: diagnostic + ciclu periodic ----------------

  // La 0,5s: reimprospatam textul "acum X secunde" si (nativ) starea serviciului - ce se trimite
  // singur in fundal trebuie sa se vada pe ecran cand il deschidem.
  useEffect(() => {
    if (!cursa) return;
    const interval = setInterval(async () => {
      forteazaRerandare(n => n + 1);
      if (NATIV_DISPONIBIL) {
        try {
          const st = await GpsTracker.getStatus();
          setPuncteNesincronizate(st.pending || 0);
          setUltimaEroareSincronizare(st.eroare || null);
          if (st.ultimaTrimitereMs) setUltimaTrimitere(st.ultimaTrimitereMs);
          setGpsActiv(st.gpsActiv !== false);

          // Cursor fluid: doar cand serviciul a primit o citire GPS NOUA (nu se face nicio cerere la server).
          if (st.lat != null && st.lon != null && st.ultimaCitireMs > ultimaCitireLiveRef.current) {
            ultimaCitireLiveRef.current = st.ultimaCitireMs;
            setPozitieLive({ lat: st.lat, lon: st.lon });
          }

          // Semnal GPS pierdut: nicio citire buna (acuratete <= 40 m) de peste 1 minut, desi GPS-ul e pornit.
          // Citirile ajung si cand stam pe loc, deci lipsa lor inseamna pierderea semnalului (nu oprire).
          if (st.fixuriContinue === true && st.gpsActiv !== false && st.ultimaCitireMs > 0) {
            const sr = semnalRef.current;
            const varsta = Date.now() - st.ultimaCitireMs;
            const pierdut = varsta > 60000;
            if (pierdut) {
              if (!sr.pierdut) { sr.pierdut = true; sr.lat = st.lat; sr.lon = st.lon; sr.la = st.ultimaCitireMs; }
              setSemnalPierdutDe(Math.round(varsta / 1000));
            } else if (sr.pierdut && st.ultimaCitireMs > sr.la) {
              const d = (sr.lat != null && st.lat != null) ? distantaMetri(sr.lat, sr.lon, st.lat, st.lon) : null;
              const secunde = Math.round((st.ultimaCitireMs - sr.la) / 1000);
              const durata = secunde >= 120 ? `${Math.round(secunde / 60)} min` : `${secunde} s`;
              sr.pierdut = false;
              setSemnalPierdutDe(null);
              setInfoSemnal({
                text: (d != null && d < 200)
                  ? 'Semnal GPS revenit — ai rămas în aceeași zonă (probabil clădire sau parcare), fără kilometri adăugați.'
                  : `Semnal GPS revenit după ~${durata}${d != null ? ` (~${(d / 1000).toFixed(1)} km în linie dreaptă, estimat)` : ''}.`,
                pana: Date.now() + 45000,
              });
            } else {
              setSemnalPierdutDe(null);
            }
          }
        } catch (e) { /* ignorat */ }
      }
    }, 500);
    return () => {
      clearInterval(interval);
      ultimaCitireLiveRef.current = 0;
      setPozitieLive(null);
    };
  }, [cursa]);

  // Cat timp cursa e activa si ecranul e pornit: trimite + actualizeaza harta la fiecare 30s.
  useEffect(() => {
    if (!cursa) return;
    const interval = setInterval(() => cicluRef.current && cicluRef.current(), INTERVAL_CICLU_ECRAN_MS);
    return () => clearInterval(interval);
  }, [cursa]);

  // Ecranul telefonului ramane pornit cat timp cursa e activa - in browser si in aplicatia Android.
  // (In APK, pe langa Wake Lock-ul web, pluginul nativ tine si el ecranul aprins - vezi efectul de mai jos.)
  async function cereWakeLock() {
    if (wakeLockRef.current) return; // deja activ
    try {
      if ('wakeLock' in navigator) {
        const lock = await navigator.wakeLock.request('screen');
        wakeLockRef.current = lock;
        // sistemul poate elibera lock-ul (economisire baterie, tab ascuns) - cand se intampla,
        // aratam starea reala; il recerem la urmatoarea ocazie (cand pagina devine vizibila).
        lock.addEventListener('release', () => {
          if (wakeLockRef.current === lock) { wakeLockRef.current = null; setEcranAprins(false); }
        });
        setEcranAprins(true);
      } else {
        setEcranAprins(false);
      }
    } catch (e) {
      // Poate esua (tab ascuns, baterie redusa, browser vechi fara suport) - nu blocam urmarirea.
      setEcranAprins(false);
    }
  }
  function elibereazaWakeLock() {
    const lock = wakeLockRef.current;
    wakeLockRef.current = null;
    try { lock?.release(); } catch (e) { /* ignorat */ }
    setEcranAprins(null);
  }

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (!NATIV_DISPONIBIL) ascunsLaRef.current = cursaRef.current ? Date.now() : null;
        return;
      }
      // devine vizibila
      if (!cursaRef.current) return;
      cereWakeLock();
      // Browser: nu putem urmari cat timp pagina e ascunsa (limitare a sistemului) - anuntam golul.
      if (!NATIV_DISPONIBIL && ascunsLaRef.current) {
        const minute = Math.round((Date.now() - ascunsLaRef.current) / 60000);
        if (minute >= 1) {
          toast(`Ai fost în afara aplicației ${minute} minut${minute === 1 ? '' : 'e'} — traseul din acel interval nu a fost înregistrat.`, 'error');
        }
        ascunsLaRef.current = null;
      }
      // Redeschiderea ecranului = O SINGURA trimitere in lot (nu mii de cereri mici).
      cicluRef.current && cicluRef.current();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cat timp exista o cursa activa pe aceasta pagina, ecranul ramane aprins: Wake Lock web (browser si
  // APK) + flag nativ FLAG_KEEP_SCREEN_ON (APK nou - nu depinde de politica WebView-ului).
  const areCursa = !!cursa;
  useEffect(() => {
    if (!areCursa) return undefined;
    cereWakeLock();
    if (NATIV_DISPONIBIL) GpsTracker.keepScreenOn({ activ: true }).catch(() => {});
    return () => {
      elibereazaWakeLock();
      if (NATIV_DISPONIBIL) GpsTracker.keepScreenOn({ activ: false }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areCursa]);

  // Sugestie pentru campul de km la Start: ultimul km de bord cunoscut al ACESTUI
  // VEHICUL (nu al soferului), editabil - poate exista o cursa intre, neinregistrata.
  useEffect(() => {
    api.get('/api/cursa-gps/ultimul-km').then(({ data }) => {
      if (data && data.km != null) setKmStart(String(data.km));
    }).catch(() => {});
  }, []);

  // ---------------- pornire / reluare ----------------

  async function pornesteNativ(cursaId) {
    const demo = localStorage.getItem('username') === 'demo';
    await GpsTracker.start({
      baseUrl: BASE_URL,
      token: localStorage.getItem('token') || '',
      cursaId,
      uploadIntervalMs: INTERVAL_TRIMITERE_FUNDAL_MS,
      // contul demo conduce "in numele" unui sofer ales - serviciul nativ trimite aceleasi antete
      demoSofer: demo ? (localStorage.getItem('demoSofer') || '') : '',
      demoVehicul: demo ? (localStorage.getItem('demoVehicul') || '') : '',
    });
  }

  // Browser: punct nou de la geolocatia browserului - filtrare pe acuratete si distanta, apoi in coada locala.
  function proceseazaPunctBrowser(cursaId, poz) {
    const acc = poz.coords.accuracy;
    if (acc != null && acc > ACURATETE_MAX_M) return; // pozitie nesigura (celule/Wi-Fi) - o aruncam
    const lat = poz.coords.latitude;
    const lon = poz.coords.longitude;
    const ts = poz.timestamp || Date.now();
    ultimaPozitieRef.current = { lat, lon };
    const ultim = ultimTrimisRef.current;
    const d = ultim ? distantaMetri(ultim.lat, ultim.lon, lat, lon) : Infinity;
    const pastreaza = !ultim || d >= DISTANTA_MIN_METRI
      || ((ts - ultim.timp) >= TIMP_LENT_MS && d >= DISTANTA_LENTA_METRI);
    if (!pastreaza) return;
    ultimTrimisRef.current = { lat, lon, timp: ts };
    const viteza = poz.coords.speed != null ? poz.coords.speed * 3.6 : null; // m/s -> km/h
    setPuncte(p => [...p, { lat, lon, timestamp: new Date(ts).toISOString() }]);
    const coada = citesteCoadaLocala();
    coada.push({ cursaId, lat, lon, viteza, acuratete: acc != null ? acc : null, ts });
    salveazaCoadaLocala(coada);
    setPuncteNesincronizate(coada.length);
  }

  function pornesteWatchBrowser(cursaId) {
    if (!('geolocation' in navigator)) {
      toast('Telefonul/browserul nu suportă geolocație.', 'error');
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (poz) => proceseazaPunctBrowser(cursaId, poz),
      (eroare) => { toast('Nu am putut citi poziția GPS: ' + eroare.message, 'error'); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
  }

  function opresteWatchBrowser() {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }

  // La montare: reia o cursa activa (a ramas pornita pe server), cu traseul de pana acum.
  // Nativ: serviciul poate rula deja (continua neatins) - il repornim doar daca nu ruleaza.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/cursa-gps/activa');
        if (data && data.id) {
          try {
            const st = JSON.parse(localStorage.getItem('cursaDestinatie') || 'null');
            if (st && Number(st.cursaId) === Number(data.id)) setDestinatie({ nume: st.nume, lat: st.lat, lon: st.lon });
          } catch (e) { /* fara destinatie salvata */ }
          setCursa(data);
          cursaRef.current = data;
          ultimIdPunctRef.current = 0;
          if (NATIV_DISPONIBIL) {
            await incarcaPuncteNoi(data.id); // traseul complet de pe server
            try {
              const st = await GpsTracker.getStatus();
              if (!st.running || Number(st.cursaId) !== Number(data.id)) await pornesteNativ(data.id);
            } catch (e) {
              toast('Urmărirea GPS nu a putut porni: ' + (e.message || e), 'error');
            }
          } else {
            const traseu = await api.get(`/api/cursa-gps/${data.id}/traseu`);
            setPuncte(traseu.data.puncte || []);
            salveazaCoadaLocala(citesteCoadaLocala().filter(i => i.cursaId === data.id));
            pornesteWatchBrowser(data.id);
          }
        }
      } catch (e) {
        // fara cursa activa - normal la prima incarcare
      } finally {
        setSeIncarca(false);
      }
    })();
    const onOnline = () => { cicluRef.current && cicluRef.current(); };
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      // Browser: fara pagina nu exista urmarire. Nativ: serviciul CONTINUA cand parasesti ecranul.
      if (!NATIV_DISPONIBIL) opresteWatchBrowser();
      elibereazaWakeLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onStart() {
    setSePorneste(true);
    try {
      let lat;
      let lon;
      if (NATIV_DISPONIBIL) {
        const pos = await GpsTracker.getCurrentPosition({ timeoutMs: 15000 });
        lat = pos.lat;
        lon = pos.lon;
        if (pos.gpsActiv === false) {
          toast('GPS-ul telefonului este oprit (locație doar din rețea) — pornește GPS-ul, altfel traseul nu se înregistrează.', 'error');
        } else if (pos.acuratete != null && pos.acuratete > ACURATETE_START_AVERTISMENT_M) {
          toast('Încă nu am semnal GPS precis (ești în clădire?). Cursa pornește, iar traseul se înregistrează de când ai semnal GPS.', 'error');
        }
      } else {
        if (!('geolocation' in navigator)) {
          toast('Telefonul/browserul nu suportă geolocație.', 'error');
          return;
        }
        const poz = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 20000 });
        });
        lat = poz.coords.latitude;
        lon = poz.coords.longitude;
      }
      const { data } = await api.post('/api/cursa-gps/start', {
        lat, lon, kmBord: kmStart.trim() !== '' ? kmStart.trim() : undefined,
      });
      setPornitAcum(true); // harta se deschide pe ecran complet (cursa tocmai pornita)
      setRuta(null);
      setRutaEroare(null);
      ultimaRecalcRef.current = 0;
      deviatiiRef.current = 0;
      if (destinatie) {
        localStorage.setItem('cursaDestinatie', JSON.stringify({ cursaId: data.id, ...destinatie }));
      }
      setCursa(data);
      cursaRef.current = data;
      setPuncte([]);
      ultimIdPunctRef.current = 0;
      ultimTrimisRef.current = null;
      ultimaPozitieRef.current = { lat, lon };
      setUltimaTrimitere(null);
      setUltimaEroareSincronizare(null);
      setPuncteNesincronizate(0);
      if (NATIV_DISPONIBIL) {
        try {
          await pornesteNativ(data.id);
          toast('Cursă GPS pornită.');
        } catch (e) {
          toast('Cursa a fost creată, dar urmărirea GPS nu a pornit: ' + (e.message || e) +
            ' Acordă permisiunea de locație și redeschide ecranul Cursă GPS.', 'error');
        }
      } else {
        salveazaCoadaLocala([]);
        pornesteWatchBrowser(data.id);
        toast('Cursă GPS pornită.');
      }
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Nu am putut porni cursa GPS.', 'error');
    } finally {
      setSePorneste(false);
    }
  }

  // ---------------- destinatie / navigatie (ajutor vizual) ----------------

  // Cel mai apropiat punct al traseului de pozitia data: { dist (m), idx }.
  function celMaiAproape(poz, coord) {
    let dist = Infinity;
    let idx = 0;
    for (let i = 0; i < coord.length; i++) {
      const d = distantaMetri(poz.lat, poz.lon, coord[i][0], coord[i][1]);
      if (d < dist) { dist = d; idx = i; }
    }
    return { dist, idx };
  }

  async function cautaDestinatie() {
    if (qDest.trim().length < 3) { toast('Scrie cel puțin 3 caractere.', 'error'); return; }
    setSeCauta(true);
    setRezultateDest(null);
    try {
      const { data } = await api.get('/api/navigatie/cauta', { params: { q: qDest.trim() } });
      setRezultateDest(data || []);
      if (!data || data.length === 0) toast('Nu am găsit nicio adresă cu acest text.', 'error');
    } catch (e) {
      toast(e.response?.data?.error || 'Nu am putut căuta adresa.', 'error');
    } finally {
      setSeCauta(false);
    }
  }

  function alegeDestinatie(r) {
    setDestinatie({ nume: r.nume, lat: r.lat, lon: r.lon });
    setRezultateDest(null);
    setQDest('');
  }

  function stergeDestinatie() {
    setDestinatie(null);
    setRuta(null);
    setRutaEroare(null);
    deviatiiRef.current = 0;
    localStorage.removeItem('cursaDestinatie');
  }

  async function calculeazaRuta(dinLat, dinLon) {
    if (!destinatie) return;
    ultimaRecalcRef.current = Date.now();
    try {
      const { data } = await api.get('/api/navigatie/traseu', {
        params: { latStart: dinLat, lonStart: dinLon, latDest: destinatie.lat, lonDest: destinatie.lon },
      });
      let totalM = 0;
      for (let i = 0; i < data.coordonate.length - 1; i++) {
        totalM += distantaMetri(data.coordonate[i][0], data.coordonate[i][1], data.coordonate[i + 1][0], data.coordonate[i + 1][1]);
      }
      setRuta({ coord: data.coordonate, distantaKm: data.distantaKm, durataMin: data.durataMin, totalM });
      setRutaEroare(null);
      deviatiiRef.current = 0;
    } catch (e) {
      // la o recalculare esuata pastram traseul vechi; eroarea se arata doar cand nu avem niciun traseu
      setRutaEroare(prev => (ruta ? null : (e.response?.data?.error || 'Nu am putut calcula traseul.')));
    }
  }

  // Cat mai e pana la destinatie, estimat din pozitia curenta pe traseul calculat.
  function infoRuta() {
    if (!ruta) return null;
    if (puncte.length === 0) return { km: ruta.distantaKm, min: ruta.durataMin };
    const ultim = puncte[puncte.length - 1];
    const { idx } = celMaiAproape(ultim, ruta.coord);
    let m = 0;
    for (let i = idx; i < ruta.coord.length - 1; i++) {
      m += distantaMetri(ruta.coord[i][0], ruta.coord[i][1], ruta.coord[i + 1][0], ruta.coord[i + 1][1]);
    }
    const parte = ruta.totalM > 0 ? Math.min(1, m / ruta.totalM) : 1;
    return { km: m / 1000, min: ruta.durataMin * parte };
  }

  const scurteaza = (nume) => (nume || '').split(',').slice(0, 3).join(',').trim();

  // ---------------- oprire ----------------

  // Golurile mari de semnal din traseu (vezi esteGol): cate sunt si cati km (in linie dreapta) acopera.
  function calculeazaGoluri(pcte) {
    let n = 0;
    let m = 0;
    for (let i = 1; i < pcte.length; i++) {
      if (esteGol(pcte[i - 1], pcte[i])) {
        n++;
        m += distantaMetri(pcte[i - 1].lat, pcte[i - 1].lon, pcte[i].lat, pcte[i].lon);
      }
    }
    return { n, km: m / 1000 };
  }

  // Starea locala dupa ce cursa s-a inchis (sau nu mai exista pe server).
  function curataLocal() {
    if (NATIV_DISPONIBIL) {
      GpsTracker.stop({ clear: true }).catch(() => {});
    } else {
      opresteWatchBrowser();
      salveazaCoadaLocala([]);
    }
    elibereazaWakeLock();
    setCursa(null);
    setPuncte([]);
    setCereKmStop(null);
    setKmStopValoare('');
    setPuncteNesincronizate(0);
    setUltimaEroareSincronizare(null);
    setPornitAcum(false);
    stergeDestinatie();
  }

  // Cursa NU se inchide cu puncte netrimise (altfel kilometrii lor s-ar pierde). Trimitem tot, cu
  // cateva reincercari; daca nu reuseste (fara conexiune), cursa ramane activa si urmarirea continua.
  async function golesteInainteDeStop() {
    if (NATIV_DISPONIBIL) {
      // un apel obisnuit reinnoieste token-ul daca a expirat, inainte sa-l dam serviciului nativ
      try { await api.get('/api/cursa-gps/activa'); } catch (e) { /* offline - flush va raporta eroarea */ }
    }
    let rez = await goleste();
    for (let i = 0; i < 2 && rez.pending > 0 && !rez.cursaInchisa; i++) {
      await pauza(2000);
      rez = await goleste();
    }
    if (rez.pending > 0 && !rez.cursaInchisa) {
      toast(`Nu am putut trimite ${rez.pending} puncte (${rez.eroare || 'eroare necunoscută'}). ` +
        'Verifică conexiunea și apasă din nou Stop — cursa rămâne activă și urmărirea continuă.', 'error');
      return false;
    }
    return true;
  }

  // Partajata intre incercarea normala de Stop si confirmarea din dialogul obligatoriu
  // de km bord (vezi cereKmStop mai jos) - ambele ajung aici odata ce avem tot ce ne
  // trebuie ca sa inchidem cursa cu succes.
  async function incheieCursa(lat, lon, kmBord) {
    const { data } = await api.post(`/api/cursa-gps/${cursa.id}/stop`, { lat, lon, kmBord });
    const goluri = calculeazaGoluri(puncteRef.current);
    // Urmarirea se opreste abia acum, dupa ce serverul a inchis cursa.
    curataLocal();
    // La Stop, backend-ul precompleteaza automat foaia de parcurs (vehicul, sofer, data, ore,
    // distanta, km bord, iar traseul se completeaza in cateva secunde din localitatile GPS).
    if (data.foaieParcursId) {
      setAvertismentStop({ foaieParcursId: data.foaieParcursId, distantaGpsKm: data.distantaGpsKm, goluri });
    } else {
      toast(`Cursă GPS oprită. Distanță estimată: ${Number(data.distantaGpsKm || 0).toFixed(1)} km.` +
        (goluri.n > 0 ? ` Atenție: ~${goluri.km.toFixed(1)} km fără semnal GPS (estimați în linie dreaptă).` : ''));
    }
  }

  async function incearcaStop(lat, lon) {
    try {
      await incheieCursa(lat, lon, undefined);
    } catch (e) {
      if (e.response?.data?.cereKmBord) {
        // Nu am dat km la Start - il cerem acum, obligatoriu; cursa ramane activa pe
        // server (si urmarirea continua) pana confirmam - vezi dialogul din randare.
        setCereKmStop({ lat, lon });
      } else if (e.response?.status === 404) {
        curataLocal();
        toast('Cursa GPS nu mai este activă pe server.', 'error');
      } else {
        toast(e.response?.data?.error || 'Nu am putut opri cursa GPS.', 'error');
      }
    }
  }

  async function onStop() {
    if (!cursa || seOpreste) return;
    setSeOpreste(true);
    try {
      if (!(await golesteInainteDeStop())) return;
      // Ultima pozitie cunoscuta (locul de oprire)
      let lat = null;
      let lon = null;
      if (NATIV_DISPONIBIL) {
        try {
          const st = await GpsTracker.getStatus();
          if (st.lat != null) { lat = st.lat; lon = st.lon; }
        } catch (e) { /* oprim fara coordonate */ }
      } else {
        if ('geolocation' in navigator) {
          const poz = await new Promise(resolve => {
            navigator.geolocation.getCurrentPosition(p => resolve(p), () => resolve(null),
              { enableHighAccuracy: true, timeout: 8000 });
          });
          if (poz) { lat = poz.coords.latitude; lon = poz.coords.longitude; }
        }
        if (lat == null && ultimaPozitieRef.current) {
          lat = ultimaPozitieRef.current.lat;
          lon = ultimaPozitieRef.current.lon;
        }
      }
      await incearcaStop(lat, lon);
    } catch (e) {
      toast('Nu am putut opri cursa: ' + (e.message || e), 'error');
    } finally {
      setSeOpreste(false);
    }
  }

  async function confirmaKmStop() {
    if (!cereKmStop || kmStopValoare.trim() === '') return;
    setSeSalveazaKmStop(true);
    try {
      // cursa a ramas activa cat timp s-a completat km-ul - trimitem ce s-a strans intre timp
      if (!(await golesteInainteDeStop())) return;
      await incheieCursa(cereKmStop.lat, cereKmStop.lon, kmStopValoare.trim());
    } catch (e) {
      toast(e.response?.data?.error || 'Nu am putut opri cursa GPS.', 'error');
    } finally {
      setSeSalveazaKmStop(false);
    }
  }

  if (seIncarca) return <Spinner />;

  const secundeDeLaTrimitere = ultimaTrimitere ? Math.max(0, Math.round((Date.now() - ultimaTrimitere) / 1000)) : null;
  const textTrimitere = secundeDeLaTrimitere == null
    ? null
    : (secundeDeLaTrimitere < 120
        ? `acum ${secundeDeLaTrimitere}s`
        : `acum ${Math.round(secundeDeLaTrimitere / 60)} min`);

  const liniaStatus = ultimaEroareSincronizare
    ? `⚠ ${puncteNesincronizate} puncte netrimise (${ultimaEroareSincronizare}) — reîncerc automat`
    : textTrimitere
      ? `Ultima trimitere ${textTrimitere}` +
        (puncteNesincronizate > 0 ? ` · ${puncteNesincronizate} puncte în așteptare` : '')
      : (puncteNesincronizate > 0
          ? `${puncteNesincronizate} puncte în așteptare — se trimit automat`
          : 'Se așteaptă prima poziție GPS...');
  const info = infoRuta();
  const textDestinatie = info
    ? (info.km < 0.08 ? 'Ai ajuns la destinație 🏁' : `${info.km.toFixed(1)} km · ~${Math.max(1, Math.round(info.min))} min până la destinație`)
    : null;

  return (
    <div style={{ padding: 12 }}>
      {avertismentStop && (
        <Modal title="Cursă încheiată" onClose={() => setAvertismentStop(null)}>
          <p style={{ margin: '0 0 6px' }}>
            Distanță estimată: <strong>{Number(avertismentStop.distantaGpsKm || 0).toFixed(1)} km</strong>.
          </p>
          <p style={{ margin: '0 0 14px' }}>
            Foaia de parcurs a fost precompletată automat (vehicul, șofer, dată, ore, distanță, km bord;
            traseul „de unde – până unde” apare în câteva secunde din localitățile GPS).
            Mai trebuie completate manual: <strong>Scopul deplasării, Consum</strong>.
          </p>
          {avertismentStop.goluri && avertismentStop.goluri.n > 0 && (
            <p style={{ margin: '0 0 14px', color: '#8a5a00', background: '#fff6e0', padding: 8, borderRadius: 6, fontSize: 13 }}>
              ⚠ Pe traseu au fost {avertismentStop.goluri.n} {avertismentStop.goluri.n === 1 ? 'gol' : 'goluri'} de semnal GPS,
              în total ~{avertismentStop.goluri.km.toFixed(1)} km. Distanța dintre punctele de pe fiecare parte a golului este
              inclusă în total în linie dreaptă, deci drumul real poate fi puțin mai lung — verifică km din foaie.
            </p>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn" style={{ background: '#eee' }} onClick={() => setAvertismentStop(null)}>
              Salvez, completez mai târziu
            </button>
            <button className="btn btn-primary" onClick={() => {
              const id = avertismentStop.foaieParcursId;
              setAvertismentStop(null);
              if (onNavigateToFoaie) onNavigateToFoaie(id);
            }}>
              Du-mă la foaie
            </button>
          </div>
        </Modal>
      )}
      {cereKmStop && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 20, maxWidth: 320, width: '100%' }}>
            <h4 style={{ margin: '0 0 10px' }}>Km de la bord la sosire</h4>
            <p style={{ margin: '0 0 12px', color: '#555', fontSize: 14 }}>
              N-ai dat km la plecare — am nevoie de el acum, ca să calculez automat km-ul
              de plecare din distanța GPS.
            </p>
            <input type="number" autoFocus value={kmStopValoare} onChange={e => setKmStopValoare(e.target.value)}
              placeholder="ex. 128512" style={{ width: '100%', padding: 8, boxSizing: 'border-box', marginBottom: 14 }} />
            <button className="btn-primary" disabled={seSalveazaKmStop || kmStopValoare.trim() === ''}
              onClick={confirmaKmStop} style={{ width: '100%', padding: 10 }}>
              {seSalveazaKmStop ? 'Se salvează...' : 'Confirmă și încheie cursa'}
            </button>
          </div>
        </div>
      )}
      {!cursa ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <p style={{ color: '#555', marginBottom: 16 }}>
            Apasă Start la plecare — traseul se va urmări live, iar la sosire poți
            prelua ora, traseul și distanța estimată direct în foaia de parcurs.
          </p>
          <div style={{ maxWidth: 260, margin: '0 auto 16px', textAlign: 'left' }}>
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>
              Km de la bord acum (opțional — dacă nu-l dai, ți-l cer la Stop)
            </label>
            <input type="number" value={kmStart} onChange={e => setKmStart(e.target.value)}
              placeholder="ex. 128450" style={{ width: '100%', padding: 8, boxSizing: 'border-box' }} />
          </div>
          {navActiv && (
            <div style={{ maxWidth: 320, margin: '0 auto 16px', textAlign: 'left' }}>
              <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>
                Destinație (opțional — îți arată drumul pe hartă)
              </label>
              {destinatie ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#eef6fc', borderRadius: 6, padding: '8px 10px', fontSize: 13 }}>
                  <span style={{ flex: 1 }}>🏁 {scurteaza(destinatie.nume)}</span>
                  <button type="button" onClick={stergeDestinatie}
                    style={{ border: 'none', background: 'none', color: '#999', cursor: 'pointer' }}>✕</button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input type="text" value={qDest} onChange={e => setQDest(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') cautaDestinatie(); }}
                      placeholder="ex. Strada Memorandumului, Cluj" style={{ flex: 1, padding: 8, boxSizing: 'border-box', minWidth: 0 }} />
                    <button type="button" className="btn" disabled={seCauta} onClick={cautaDestinatie}>
                      {seCauta ? '...' : 'Caută'}
                    </button>
                  </div>
                  {rezultateDest && rezultateDest.length > 0 && (
                    <div style={{ border: '1px solid #ddd', borderRadius: 6, marginTop: 6, background: '#fff' }}>
                      {rezultateDest.map((r, i) => (
                        <div key={i} onClick={() => alegeDestinatie(r)}
                          style={{ padding: '8px 10px', fontSize: 12, cursor: 'pointer', borderTop: i ? '1px solid #eee' : 'none' }}>
                          {r.nume}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <button className="btn-primary" disabled={sePorneste} onClick={onStart}
            style={{ fontSize: 16, padding: '12px 28px' }}>
            {sePorneste ? 'Se pornește...' : '▶ Start cursă'}
          </button>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <strong>Cursă activă</strong> — pornită la {cursa.oraStart || ''}
              <div style={{ color: '#555', fontSize: 13 }}>
                Distanță estimată: {Number(cursa.distantaGpsKm || 0).toFixed(1)} km
              </div>
              {/* Diagnostic vizibil: se vede direct pe ecran daca trimiterea functioneaza. */}
              <div style={{ color: ultimaEroareSincronizare ? '#b33' : '#999', fontSize: 12, marginTop: 2 }}>
                {liniaStatus}
              </div>
              {semnalPierdutDe != null && (
                <div style={{ color: '#b33', fontSize: 12, marginTop: 2 }}>
                  ⚠ Semnal GPS pierdut de {semnalPierdutDe} s — se reia automat când revine.
                </div>
              )}
              {infoSemnal && Date.now() < infoSemnal.pana && (
                <div style={{ color: '#2c7a3a', fontSize: 12, marginTop: 2 }}>{infoSemnal.text}</div>
              )}
              {destinatie && (
                <div style={{ color: '#2980b9', fontSize: 12, marginTop: 2 }}>
                  🏁 {scurteaza(destinatie.nume)}
                  {textDestinatie ? ` — ${textDestinatie}` : (rutaEroare ? '' : ' — se calculează traseul…')}
                  {' '}
                  <button type="button" onClick={stergeDestinatie}
                    style={{ border: 'none', background: 'none', color: '#999', cursor: 'pointer', fontSize: 12 }}>
                    ✕ șterge
                  </button>
                </div>
              )}
              {destinatie && rutaEroare && (
                <div style={{ color: '#b33', fontSize: 12, marginTop: 2 }}>
                  ⚠ {rutaEroare}{' '}
                  <button type="button" onClick={() => { setRutaEroare(null); ultimaRecalcRef.current = 0; }}
                    style={{ border: 'none', background: 'none', color: '#2980b9', cursor: 'pointer', fontSize: 12 }}>
                    Reîncearcă
                  </button>
                </div>
              )}
              {ecranAprins === true && (
                <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>🔆 Ecranul rămâne aprins cât timp cursa e activă.</div>
              )}
              {ecranAprins === false && (
                <div style={{ color: '#b36b00', fontSize: 11, marginTop: 2 }}>
                  ⚠ Telefonul nu permite menținerea ecranului aprins (economisire baterie?) — ecranul se poate stinge.
                </div>
              )}
              {!gpsActiv && (
                <div style={{ color: '#b33', fontSize: 12, marginTop: 2 }}>
                  ⚠ GPS-ul telefonului este oprit — pornește locația ca să se înregistreze traseul.
                </div>
              )}
            </div>
            <button className="btn-danger" disabled={seOpreste} onClick={onStop} style={{ fontSize: 15, padding: '10px 20px' }}>
              {seOpreste ? 'Se trimit punctele...' : '⏹ Stop cursă'}
            </button>
          </div>
          <CursaGpsMap
            trasee={[{ id: cursa.id, puncte }]}
            pozitieLive={pozitieLive}
            permiteNavigatie
            navigatieImplicit
            ruta={ruta ? ruta.coord : null}
            destinatie={destinatie}
            plinLaMontare={pornitAcum}
            inchideEcranComplet={!!cereKmStop}
            suprapunere={(
              <div style={{
                background: 'rgba(255,255,255,0.96)', borderRadius: 8, padding: '8px 10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.35)', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', gap: 8,
              }}>
                <div style={{ fontSize: 12, minWidth: 0 }}>
                  <div><b>{Number(cursa.distantaGpsKm || 0).toFixed(1)} km</b> parcurși</div>
                  {destinatie && (
                    <div style={{ color: '#2980b9' }}>
                      🏁 {textDestinatie || (rutaEroare ? rutaEroare : 'se calculează traseul…')}
                    </div>
                  )}
                  {semnalPierdutDe != null && (
                    <div style={{ color: '#b33' }}>⚠ Semnal GPS pierdut de {semnalPierdutDe} s</div>
                  )}
                  {infoSemnal && Date.now() < infoSemnal.pana && (
                    <div style={{ color: '#2c7a3a' }}>{infoSemnal.text}</div>
                  )}
                  <div style={{ color: ultimaEroareSincronizare ? '#b33' : '#888' }}>{liniaStatus}</div>
                </div>
                <button className="btn-danger" disabled={seOpreste} onClick={onStop}
                  style={{ fontSize: 14, padding: '8px 14px', whiteSpace: 'nowrap' }}>
                  {seOpreste ? 'Se trimit...' : '⏹ Stop'}
                </button>
              </div>
            )}
          />
        </div>
      )}
      <div style={{ borderTop: '1px solid #eee', marginTop: 20, paddingTop: 14 }}>
        <h4 style={{ margin: '0 0 10px' }}>Istoricul curselor mele</h4>
        <IstoricCurse endpoint="/api/cursa-gps/istoric" />
      </div>
    </div>
  );
}

// ==================== VEDERE DISPECER / ADMIN ====================

function VedereDispecer() {
  const [curse, setCurse] = useState([]);
  const [seIncarca, setSeIncarca] = useState(true);
  const [traseeSelectate, setTraseeSelectate] = useState({}); // { cursaId: [puncte] }
  const [eroareIncarcare, setEroareIncarcare] = useState(null);

  // v68: o cerere esuata tacut (token expirat, retea) arata IDENTIC cu "nimeni nu e in
  // cursa" - exact ca la bug-ul cu punctele netrimise. Pastram reincercarea periodica
  // tacuta (fara toast la fiecare 5s), dar aratam clar pe ecran daca ULTIMA incercare a
  // esuat, ca sa nu se mai confunde o eroare cu o lista goala.
  const incarca = useCallback(async () => {
    try {
      const { data } = await api.get('/api/cursa-gps/active');
      setCurse(data);
      setEroareIncarcare(null);
    } catch (e) {
      setEroareIncarcare(e.response ? `${e.response.status} ${e.response.data?.error || e.response.statusText || ''}`.trim() : 'eroare de conexiune');
    } finally {
      setSeIncarca(false);
    }
  }, []);

  useEffect(() => {
    incarca();
    const interval = setInterval(incarca, 5000);
    return () => clearInterval(interval);
  }, [incarca]);

  // Aducem traseul complet pentru fiecare cursa activa, ca sa desenam linia pe harta
  // (nu doar ultimul punct) - la fiecare reincarcare a listei.
  useEffect(() => {
    (async () => {
      const rezultate = {};
      await Promise.all(curse.map(async (c) => {
        try {
          const { data } = await api.get(`/api/cursa-gps/${c.id}/traseu`);
          rezultate[c.id] = data.puncte || [];
        } catch {
          rezultate[c.id] = [];
        }
      }));
      setTraseeSelectate(rezultate);
    })();
  }, [curse]);

  if (seIncarca) return <Spinner />;

  // Fara "culoare" fixa aici - CursaGpsMap ii aloca fiecarui vehicul o culoare din
  // paleta, stabila intre reimprospatari (vezi CursaGpsMap.js), ca sa se distinga
  // usor mai multe curse active simultan pe aceeasi harta.
  const trasee = curse.map(c => ({
    id: c.id,
    puncte: traseeSelectate[c.id] || [],
    pozitieCurenta: c.ultimPunct ? { lat: c.ultimPunct.lat, lon: c.ultimPunct.lon } : null,
    eticheta: `${c.nrInmatriculare} — ${c.username}`,
  }));

  return (
    <div style={{ padding: 12 }}>
      {eroareIncarcare && (
        <p style={{ color: '#b33', textAlign: 'center', padding: '8px 0', fontSize: 13 }}>
          ⚠ Nu am putut verifica cursele active (eroare: {eroareIncarcare}) — lista de mai jos poate fi învechită.
        </p>
      )}
      {curse.length === 0 ? (
        <p style={{ color: '#888', textAlign: 'center', padding: 24 }}>
          {eroareIncarcare ? 'Nu am putut încărca lista — vezi eroarea de mai sus.' : 'Nicio cursă GPS activă în acest moment.'}
        </p>
      ) : (
        <>
          <CursaGpsMap trasee={trasee} inaltime={420} />
          <table style={{ width: '100%', marginTop: 14, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: 6 }}>Vehicul</th>
                <th style={{ padding: 6 }}>Șofer</th>
                <th style={{ padding: 6 }}>Pornit la</th>
                <th style={{ padding: 6 }}>Distanță estimată</th>
              </tr>
            </thead>
            <tbody>
              {curse.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 6 }}>{c.nrInmatriculare}</td>
                  <td style={{ padding: 6 }}>{c.username}</td>
                  <td style={{ padding: 6 }}>{c.oraStart || ''}</td>
                  <td style={{ padding: 6 }}>{Number(c.distantaGpsKm || 0).toFixed(1)} km</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <div style={{ borderTop: '1px solid #eee', marginTop: 20, paddingTop: 14 }}>
        <h4 style={{ margin: '0 0 10px' }}>Istoricul tuturor curselor</h4>
        <IstoricCurse endpoint="/api/cursa-gps/istoric-toate" arataVehiculSofer />
      </div>
    </div>
  );
}

// v76: contul DEMO nu are vehicul asignat si nu e "un sofer" - alege aici in numele CUI (si cu ce
// vehicul) face cursa. Cursa se inregistreaza pe soferul ales, ca si cum acesta ar fi logat.
function CursaDemo({ onNavigateToFoaie }) {
  const [soferi, setSoferi] = useState(null);   // null = se incarca
  const [vehicule, setVehicule] = useState([]);
  const [sofer, setSofer] = useState('');
  const [vehicul, setVehicul] = useState('');
  const [blocat, setBlocat] = useState(false);  // true cat timp exista o cursa activa

  // Alegerea se retine si in localStorage - de acolo o iau antetele trimise de api.js.
  function alege(nouSofer, nouVehicul) {
    localStorage.setItem('demoSofer', nouSofer);
    localStorage.setItem('demoVehicul', nouVehicul);
    setSofer(nouSofer);
    setVehicul(nouVehicul);
  }

  useEffect(() => {
    Promise.all([api.get('/api/personal/soferi'), api.get('/api/auto/nruri')]).then(([s, v]) => {
      const lista = (s.data || []).filter(x => x.adminu === 'conducator_auto');
      const nruri = (v.data || []).map(x => x.nrInmatriculare);
      setSoferi(lista);
      setVehicule(nruri);
      const salvat = lista.find(x => x.username === localStorage.getItem('demoSofer')) || lista[0];
      if (!salvat) return;
      const vSalvat = localStorage.getItem('demoVehicul');
      const vInitial = (vSalvat && nruri.includes(vSalvat)) ? vSalvat
        : (salvat.nrInmatriculareAsignat && nruri.includes(salvat.nrInmatriculareAsignat) ? salvat.nrInmatriculareAsignat : (nruri[0] || ''));
      alege(salvat.username, vInitial);
    }).catch(() => setSoferi([]));
  }, []);

  function schimbaSofer(username) {
    const sf = (soferi || []).find(x => x.username === username);
    // vehiculul soferului, daca are unul asignat; altfel ramane cel ales
    const v = sf && sf.nrInmatriculareAsignat && vehicule.includes(sf.nrInmatriculareAsignat)
      ? sf.nrInmatriculareAsignat : vehicul;
    alege(username, v);
  }

  if (soferi === null) return <Spinner />;

  const pregatit = sofer && vehicul;
  return (
    <div>
      <div style={{ padding: '12px 12px 0' }}>
        <h4 style={{ margin: '0 0 8px' }}>Cursa mea (demo)</h4>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 2 }}>Cursă făcută de</label>
            <select value={sofer} disabled={blocat} onChange={e => schimbaSofer(e.target.value)} style={{ padding: 6 }}>
              {soferi.length === 0 && <option value="">— niciun șofer —</option>}
              {soferi.map(x => <option key={x.username} value={x.username}>{x.username}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 2 }}>Cu vehiculul</label>
            <select value={vehicul} disabled={blocat} onChange={e => alege(sofer, e.target.value)} style={{ padding: 6 }}>
              {vehicule.length === 0 && <option value="">— niciun vehicul —</option>}
              {vehicule.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        {blocat && (
          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
            Șoferul și vehiculul nu se pot schimba cât timp cursa este activă.
          </div>
        )}
      </div>
      {pregatit ? (
        <VedereSofer key={sofer + '|' + vehicul} onNavigateToFoaie={onNavigateToFoaie} onCursaActiva={setBlocat} />
      ) : (
        <div style={{ padding: 16, color: '#888' }}>
          Nu există șoferi (conducători auto) sau vehicule active în demo — adaugă-le în Administrare.
        </div>
      )}
      <div style={{ padding: '0 12px', borderTop: '1px solid #eee', marginTop: 8 }}>
        <h4 style={{ margin: '14px 0 4px' }}>Toate cursele active</h4>
      </div>
      <VedereDispecer />
    </div>
  );
}

export default function CursaGpsTab({ onNavigateToFoaie }) {
  if (localStorage.getItem('username') === 'demo') return <CursaDemo onNavigateToFoaie={onNavigateToFoaie} />;
  const rol = localStorage.getItem('rol') || 'Level_0';
  const vehiculAsignat = localStorage.getItem('vehiculAsignat') || '';
  // v68: cele doua vederi nu mai sunt exclusive. "Poate porni propria cursa" tine
  // strict de daca are un vehicul asignat (indiferent de rol - vezi AdminController:
  // un admin poate avea, optional, si el masina). "Vede harta cu toti ceilalti"
  // tine de rol, la fel ca pana acum - un conducator auto "pur" nu vede cursele
  // altora, dar un admin cu masina le vede pe ale lui SI pe ale tuturor.
  const poateConduce = !!vehiculAsignat;
  const esteDispecerSauAdmin = rol !== 'conducator_auto';

  if (poateConduce && esteDispecerSauAdmin) {
    return (
      <div>
        <div style={{ padding: '12px 12px 0' }}>
          <h4 style={{ margin: '0 0 4px' }}>Cursa mea</h4>
        </div>
        <VedereSofer onNavigateToFoaie={onNavigateToFoaie} />
        <div style={{ padding: '0 12px', borderTop: '1px solid #eee', marginTop: 8 }}>
          <h4 style={{ margin: '14px 0 4px' }}>Toate cursele active</h4>
        </div>
        <VedereDispecer />
      </div>
    );
  }
  return poateConduce ? <VedereSofer onNavigateToFoaie={onNavigateToFoaie} /> : <VedereDispecer />;
}
